/**
 * RankingCardModal — modal Admin que gera o card semanal/mensal do
 * pódio top-3 offroad. Usa: GET /api/admin/ranking-card?period=week|month
 *
 * Auto-gerado com base nos passport_stamps do período. Pensado para
 * partilha recorrente nas segundas de manhã ("Ranking Semanal Tectur").
 *
 * Erros de geração são visíveis no modal E também reportados ao Error
 * Monitor (POST /api/errors/report) para diagnóstico rápido pelo admin.
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { reportError } from '@/lib/errorReporter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RankingCardModal({ open, onClose }) {
  const [period, setPeriod] = useState('week');
  const [downloading, setDownloading] = useState(false);
  const [imgError, setImgError] = useState(null);
  const cacheBuster = useMemo(() => (open ? Date.now() : 0), [open, period]);

  const url = `${API}/admin/ranking-card?period=${period}&_=${cacheBuster}`;

  const handleImgError = async () => {
    // <img> não expõe status HTTP → fetch paralelo para extrair o detail.
    let detail = 'HTTP failure';
    let status = 0;
    try {
      const res = await fetch(url, { credentials: 'include' });
      status = res.status;
      const body = await res.text();
      try { detail = JSON.parse(body).detail || body.slice(0, 200); }
      catch { detail = body.slice(0, 200); }
    } catch (e) {
      detail = e?.message || 'network error';
    }
    setImgError({ status, detail });
    toast.error(`Ranking falhou (${status}): ${detail}`);
    reportError({
      level: 'error',
      category: 'ranking_card_render',
      message: `Ranking card ${period} falhou: ${detail}`,
      url: `/admin/ranking-card?period=${period}`,
      http_status: status || undefined,
    });
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `tectur_ranking_${period}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
      toast.success('📥 Ranking descarregado');
    } catch (e) {
      const msg = e?.message || 'Falha desconhecida';
      toast.error(`Falha a gerar o ranking: ${msg}`);
      reportError({
        level: 'error',
        category: 'ranking_card_download',
        message: `Ranking card download falhou: ${msg}`,
        url: `/admin/ranking-card?period=${period}`,
      });
    } finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="ranking-card-dialog"
      >
        <DialogHeader>
          <DialogTitle style={{
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: '2px', textTransform: 'uppercase', color: '#F2EBDD',
          }}>
            🏆 Ranking · Card para redes sociais
          </DialogTitle>
          <DialogDescription>
            Top-3 utilizadores por km-on-route no período. Auto-gerado para
            partilhares nas segundas — sem esforço.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mt-2 mb-3">
          {[{ id: 'week', label: 'Semana passada' },
            { id: 'month', label: 'Mês passado' }].map((p) => (
            <button
              key={p.id}
              onClick={() => { setPeriod(p.id); setImgError(null); }}
              data-testid={`ranking-period-${p.id}`}
              style={{
                padding: '8px 14px', borderRadius: 4,
                border: `1px solid ${period === p.id ? '#E87A2A' : '#3D362E'}`,
                background: period === p.id ? '#E87A2A' : 'transparent',
                color: period === p.id ? '#0F0D0B' : '#C4A275',
                fontSize: 11, fontWeight: 800, letterSpacing: 1,
                fontFamily: "'JetBrains Mono', monospace",
                textTransform: 'uppercase', cursor: 'pointer',
              }}
            >{p.label}</button>
          ))}
        </div>

        <div style={{
          background: '#0F0D0B', border: '1px solid #3D362E',
          borderRadius: 6, padding: 12, display: 'flex',
          justifyContent: 'center', minHeight: 300,
        }}>
          <img
            key={`${period}-${cacheBuster}`}
            src={url}
            alt={`Ranking ${period}`}
            data-testid={`ranking-preview-${period}`}
            style={{ maxWidth: 480, width: '100%', height: 'auto',
                     display: imgError ? 'none' : 'block' }}
            onLoad={() => setImgError(null)}
            onError={handleImgError}
          />
          {imgError && (
            <div
              data-testid="ranking-error-box"
              style={{
                padding: 20, textAlign: 'center',
                color: '#EF4444', fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12, lineHeight: 1.6,
              }}
            >
              ⚠️ FALHA A GERAR O CARD<br />
              <span style={{ color: '#C4A275', fontSize: 11 }}>
                HTTP {imgError.status || '?'} · {imgError.detail}
              </span><br />
              <span style={{ color: '#8a7f6f', fontSize: 10, marginTop: 8, display: 'inline-block' }}>
                Este erro já foi enviado para o painel ERROS · secção admin.
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-3">
          <div className="flex-1 text-xs text-muted-foreground"
               style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            1080×1350 · Instagram feed portrait · pronto a partilhar
          </div>
          <Button
            size="sm"
            onClick={download}
            disabled={downloading}
            data-testid="ranking-download-btn"
            style={{
              background: '#E87A2A', color: '#0F0D0B',
              fontSize: 11, fontWeight: 800, letterSpacing: 1,
              fontFamily: "'JetBrains Mono', monospace",
              textTransform: 'uppercase',
            }}
          >
            {downloading ? '⏳' : '📥'} DESCARREGAR
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
